import { useState } from "react";
import { AuthPageLayout } from "./AuthPageLayout";
import { LoadingButton } from "./LoadingButton";
import { PasswordField } from "./PasswordField";
import { PasswordRequirementsHint } from "./PasswordRequirementsHint";
import {
  REISSUE_CODE_LENGTH,
  REISSUE_SENT_MESSAGE,
  REISSUE_SENT_NOTE,
} from "../resetPasswordTarget";

/**
 * PRODUCT_DEV-1238 メールで戻せない人のパスワード再発行（ログイン画面側の2画面）。
 * 依頼の確認の画面は置かない（2026-10-26 案C。「パスワードを再発行する」で直接受け付ける）。
 * 見た目は今の ForgotPasswordUI / ResetPasswordUI を写している（UI 案 A・2026-10-26 合意）。
 */

const backButtonClass =
  "w-full bg-gray-500 hover:bg-gray-600 text-white py-4 rounded-2xl text-xl font-bold transition-colors";
const linkButtonClass = "w-full text-center text-sm font-bold text-primary py-2";
const inputClass =
  "w-full px-6 py-4 border border-neutral-300 rounded-2xl text-neutral-900 placeholder-neutral-400 focus:outline-none focus:border-primary transition-colors";

/** 押したあと（メールで戻せなかった人。誰の ID でも同じ） */
export const ReissueSentUI: React.FC<{
  onBackToLogin: () => void;
  onOpenCode: () => void;
}> = ({ onBackToLogin, onOpenCode }) => (
  <AuthPageLayout>
    <h2 className="text-3xl font-bold text-center text-neutral-900 mb-4">受け付けました</h2>
    <p className="text-center text-neutral-600">{REISSUE_SENT_MESSAGE}</p>
    <p className="text-center text-xs text-neutral-500 mt-2 mb-8">{REISSUE_SENT_NOTE}</p>
    <div className="space-y-3">
      <button
        type="button"
        onClick={onBackToLogin}
        className="w-full bg-primary hover:opacity-90 text-white py-4 rounded-2xl text-xl font-bold transition-colors"
      >
        ログインに戻る
      </button>
      <button type="button" onClick={onOpenCode} className={linkButtonClass}>
        管理者からコードを受け取った方
      </button>
    </div>
  </AuthPageLayout>
);

/** コードで新しいパスワードを決める（ID は会社の独自ID かメールアドレス） */
export const ReissueCodeUI: React.FC<{
  loading: boolean;
  error: string | null;
  loginId: string;
  /** 欄の呼び方（`buildReissueIdLabel`。例「行員番号またはメールアドレス」）。 */
  idLabel: string;
  code: string;
  newPassword: string;
  confirmPassword: string;
  onLoginIdChange: (v: string) => void;
  onCodeChange: (v: string) => void;
  onNewPasswordChange: (v: string) => void;
  onConfirmPasswordChange: (v: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  onBack: () => void;
}> = ({
  loading,
  error,
  loginId,
  idLabel,
  code,
  newPassword,
  confirmPassword,
  onLoginIdChange,
  onCodeChange,
  onNewPasswordChange,
  onConfirmPasswordChange,
  onSubmit,
  onBack,
}) => {
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  return (
    <AuthPageLayout>
      <h2 className="text-3xl font-bold text-center text-neutral-900 mb-4">新しいパスワードを設定</h2>
      <p className="text-center text-neutral-600 mb-8">
        ログイン用コードと新しいパスワードを入力してください。
      </p>
      <form onSubmit={onSubmit} className="space-y-6">
        <div>
          <label htmlFor="reissue-code-login-id" className="block text-sm font-medium text-neutral-700 mb-2">
            {idLabel}
          </label>
          <input
            id="reissue-code-login-id"
            type="text"
            autoComplete="username"
            placeholder={idLabel}
            value={loginId}
            onChange={(e) => onLoginIdChange(e.target.value)}
            className={`${inputClass} bg-white`}
            disabled={loading}
            required
          />
        </div>
        <div>
          <label htmlFor="reissue-code" className="block text-sm font-medium text-neutral-700 mb-2">
            ログイン用コード（{REISSUE_CODE_LENGTH}文字）
          </label>
          {/* 英大小・数字・記号が混ざる。6桁数字専用の CodeInputField は使えない */}
          <input
            id="reissue-code"
            type="text"
            autoComplete="one-time-code"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            maxLength={REISSUE_CODE_LENGTH + 2}
            placeholder="管理者から受け取ったコード"
            value={code}
            onChange={(e) => onCodeChange(e.target.value)}
            className={`${inputClass} bg-white font-mono tracking-widest`}
            disabled={loading}
            required
          />
        </div>
        <PasswordField
          id="reissue-new-password"
          name="new-password"
          autoComplete="new-password"
          label="新しいパスワード"
          placeholder="新しいパスワードを入力"
          value={newPassword}
          onChange={onNewPasswordChange}
          show={showNew}
          onToggle={() => setShowNew((v) => !v)}
          disabled={loading}
        />
        <PasswordField
          id="reissue-confirm-password"
          name="confirm-password"
          autoComplete="new-password"
          label="パスワード確認"
          placeholder="もう一度入力してください"
          value={confirmPassword}
          onChange={onConfirmPasswordChange}
          show={showConfirm}
          onToggle={() => setShowConfirm((v) => !v)}
          disabled={loading}
        />
        <PasswordRequirementsHint />
        {error && <div className="text-sm text-red-600">{error}</div>}
        <div className="space-y-3">
          <LoadingButton loading={loading} loadingMessage="設定中..." label="パスワードを設定する" />
          <button type="button" onClick={onBack} className={backButtonClass} disabled={loading}>
            戻る
          </button>
        </div>
      </form>
    </AuthPageLayout>
  );
};
